import helper from '../helpers/ApiUrlHelper.js';
import store from '../store/index.js';
import { getCookie, setCookie } from '../helpers/CookieHelper.js';
import { readUtm } from '../helpers/UtmHelper.js';
import { readReferrerAttribution } from '../helpers/ReferrerHelper.js';
import {
  hasStatisticsConsent,
  consentDecisionMade
} from '../helpers/ConsentHelper.js';

// Page events always fire. Consent (cookie_prefs.consent === 'all') only controls
// whether an event carries a session token. Referrer attribution is persisted
// once per visitor flow so internal SPA navigation cannot overwrite it.

const SESSION_COOKIE = 'hp_sid';
const SESSION_TTL_DAYS = 30;

// Events emitted before the visitor accepts/rejects the cookie banner are held
// in memory (NOT persisted) and flushed once a decision is made, so the very
// first hit can be linked to the session once a token exists.
let pendingEvents = [];
let pendingPageView = null;

// Buffering is armed by cookie-banner.vue when our banner or Usercentrics is
// the consent gate.
let bufferingArmed = false;

function getSessionToken() {
  // A token only exists WITH statistics consent, so anonymous events stay
  // unlinkable. It lives in a cookie so it is stable across reloads and tabs.
  if (!hasStatisticsConsent()) {
    return '';
  }

  let token = getCookie(SESSION_COOKIE);
  if (token) {
    return token;
  }

  if (window.crypto?.randomUUID) {
    token = window.crypto.randomUUID();
  } else {
    const bytes = window.crypto?.getRandomValues
      ? window.crypto.getRandomValues(new Uint8Array(16))
      : Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));

    token = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  setCookie(SESSION_COOKIE, token, SESSION_TTL_DAYS);
  return token;
}

function trackGoogleAnalytics(path, title) {
  if (!hasStatisticsConsent()) {
    return;
  }

  const state = store.state.companyModule;
  if (!state.useGoogleAnalytics) {
    return;
  }

  const gaId = state.googleAnalyticsId || state.googleAnalyticsIdentifier;
  if (!gaId) {
    return;
  }

  if (!window.ga) {
    return;
  }

  window.ga('create', gaId, 'auto');
  window.ga('set', 'anonymizeIp', true);
  window.ga('set', 'page', path);
  window.ga('send', 'pageview', { title });
}

function trackMatomo(path, title, eventType, searchObject) {
  if (!hasStatisticsConsent()) {
    return;
  }

  const state = store.state.companyModule;
  if (!state.useMatomo) {
    return;
  }

  try {
    window._mtm = window._mtm || [];

    if (searchObject) {
      window._mtm.push(searchObject);
    } else {
      window._mtm.push({
        event: eventType,
        page_title: title,
        page_location: window.location.href,
        page_path: path
      });
    }
  } catch (error) {
    // Matomo tracking failures should not block navigation
    // eslint-disable-next-line no-console
    console.warn('Matomo tracking failed', error);
  }
}

// Builds the anonymous part of the payload (safe to send without consent).
// UTM and referrer attribution are always included; the session token is added
// only with statistics consent.
function buildBasePayload(path, title, to, eventType, jobId, searchObject) {
  const state = store.state;
  const companyState = store.state.companyModule;
  const payload = {
    path,
    name: to?.name,
    title,
    slug: to?.params?.slug || companyState.slug,
    companyId: companyState.currentCompanyId,
    language: state.currentLanguage,
    timestamp: new Date().toISOString(),
    event: eventType,
    host: window.location.host,
    jobofferId: jobId,
    referrer: readReferrerAttribution(),
    sessionToken: ''
  };

  if (searchObject) {
    payload.searchData = JSON.stringify(searchObject);
  }

  const utm = readUtm();
  if (utm) {
    payload.utmSource = utm.source;
    payload.utmMedium = utm.medium;
    payload.utmCampaign = utm.campaign;
    payload.utmTerm = utm.term;
    payload.utmContent = utm.content;
    payload.gclid = utm.gclid;
    payload.fbclid = utm.fbclid;
    payload.msclkid = utm.msclkid;
  }

  return payload;
}

// Adds the session token when statistics consent is present.
function decorateWithConsent(payload) {
  if (!hasStatisticsConsent()) {
    return payload;
  }

  const consented = { ...payload };
  consented.sessionToken = getSessionToken();
  return consented;
}

function emit(payload) {
  const apiUrl = helper.apiUrl();
  if (!apiUrl) {
    return;
  }

  // keepalive lets the request survive a page unload (used by the flush-on-leave
  // safety net) while still going through normal CORS to the company API origin.
  fetch(`${apiUrl}/tracking/page-event`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    keepalive: true
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn('Backend tracking failed', error);
  });
}

function trackBackend(
  path,
  title,
  to,
  eventType,
  jobId = null,
  searchObject = null
) {
  const payload = buildBasePayload(
    path,
    title,
    to,
    eventType,
    jobId,
    searchObject
  );

  // Before the consent decision (and only while our banner governs consent):
  // hold the event so it can be linked (or sent anonymously) once the visitor chooses.
  if (bufferingArmed && !consentDecisionMade()) {
    pendingEvents.push(payload);
    return;
  }

  emit(decorateWithConsent(payload));
}

// Flushes buffered pre-decision events and stamps each with how many were queued
// at the moment of the decision (bufferedEventCount) as a consent-latency proxy.
function flushPending(consented) {
  bufferingArmed = false;
  const bufferedEventCount = pendingEvents.length;
  if (bufferedEventCount === 0) {
    return;
  }

  const events = pendingEvents;
  pendingEvents = [];

  events.forEach((payload) => {
    const base = { ...payload, bufferedEventCount };
    emit(consented ? decorateWithConsent(base) : base);
  });
}

function flushOnLeave() {
  if (pendingEvents.length === 0) {
    return;
  }

  // Left without deciding -> send anonymously (unlinked). If a decision was made,
  // honor it.
  flushPending(consentDecisionMade() && hasStatisticsConsent());
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushOnLeave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushOnLeave();
    }
  });
}

function getRouteJobofferId(to) {
  const value = to?.params?.jobofferId || to?.params?.jobId;
  const parsedValue = Number.parseInt(value, 10);

  return Number.isInteger(parsedValue) && parsedValue > 0
    ? parsedValue
    : null;
}

function trackPageView(to /*, from */) {
  const path = to?.fullPath || window.location.pathname;
  const title = document.title;

  trackGoogleAnalytics(path, title);
  trackBackend(path, title, to, 'page_view', getRouteJobofferId(to));
  trackMatomo(path, title, 'page_view');
}

export default {
  // Arm pre-consent buffering. Called by cookie-banner.vue when a supported
  // consent gate can resolve the queued events.
  armConsentBuffering() {
    bufferingArmed = true;
  },

  // Flush buffered pre-consent events. consented = true after accept, false after
  // reject. Exposed so cookie-banner.vue can call it on the consent decision.
  flushPendingEvents(consented) {
    flushPending(consented);
  },

  queuePageView(to, from) {
    pendingPageView = { to, from };
  },

  flushPageView() {
    if (!pendingPageView) {
      return;
    }

    const pageView = pendingPageView;
    pendingPageView = null;
    trackPageView(pageView.to /*, pageView.from */);
  },

  async trackApplyCta(jobId, ctaType) {
    const path = window.location.pathname;
    const title = document.title;
    const trackingData = {
      event: 'application_cta_click',
      cta_type: ctaType,
      joboffer_id: jobId
    };

    trackGoogleAnalytics(path, title);
    trackBackend(
      path,
      title,
      null,
      'application_cta_click',
      jobId,
      trackingData
    );
    trackMatomo(path, title, 'application_cta_click', trackingData);
  },

  async trackApplyCancel(jobId) {
    const path = window.location.pathname;
    const title = document.title;

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'application_close', jobId);
    trackMatomo(path, title, 'application_close');
  },

  async trackApplySubmit(jobId) {
    const path = window.location.pathname;
    const title = document.title;

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'application_form_submit', jobId);
    trackMatomo(path, title, 'application_form_submit');
  },

  async trackApplyStart(jobId) {
    const path = window.location.pathname;
    const title = document.title;

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'application_form_start', jobId);
    trackMatomo(path, title, 'application_form_start');
  },

  async trackJobalertCta() {
    const path = window.location.pathname;
    const title = document.title;

    trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'jobalert_cta_click');
    trackMatomo(path, title, 'jobalert_cta_click');
  },

  async trackJobalertClose() {
    const path = window.location.pathname;
    const title = document.title;

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'jobalert_close');
    trackMatomo(path, title, 'jobalert_close');
  },

  async trackJobalertSubmit() {
    const path = window.location.pathname;
    const title = document.title;

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'jobalert_form_submit');
    trackMatomo(path, title, 'jobalert_form_submit');
  },

  async trackJobalertFormStart() {
    const path = window.location.pathname;
    const title = document.title;

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'jobalert_form_start');
    trackMatomo(path, title, 'jobalert_form_start');
  },

  async trackJobofferSearch(keyword, resultsCount) {
    const path = window.location.pathname;
    const title = document.title;

    const searchObject = {
      event: 'search',
      search_term: keyword,
      search_count: resultsCount,
      search_category: 'job'
    };

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'search', null, searchObject);
    trackMatomo(path, title, 'search', searchObject);
  },

  async trackFilterChange(filters, resultsCount, totalCount) {
    const path = window.location.pathname;
    const title = document.title;

    const filterObject = {
      event: 'filter_change',
      filters,
      result_count: resultsCount,
      total_count: totalCount,
      active_filter_count: Object.values(filters).reduce(
        (sum, items) => sum + items.length,
        0
      )
    };

    // trackGoogleAnalytics(path, title);
    trackBackend(path, title, null, 'filter_change', null, filterObject);
    trackMatomo(path, title, 'filter_change', filterObject);
  }
};
